import type {
  Workspace,
  WorkspaceAddress,
  WorkspaceCreatePayload,
  WorkspacePlan,
  WorkspacePlanCreatePayload,
  WorkspacePlanUpdatePayload,
  WorkspaceSettings,
  WorkspaceSettingsUpdatePayload,
  WorkspaceStats,
  WorkspaceUpdatePayload,
} from "@/types/workspace";

import { api, unwrap } from "./axiosConfig";

const BASE = "/api/v1/workspaces";

export const workspaceService = {
  async listMine(): Promise<Workspace[]> {
    return unwrap(api.get(`${BASE}/me`));
  },

  async get(id: number): Promise<Workspace> {
    return unwrap(api.get(`${BASE}/${id}`));
  },

  async create(payload: WorkspaceCreatePayload): Promise<Workspace> {
    return unwrap(api.post(BASE, payload));
  },

  /** Platform-admin manual creation: workspace + owner; owner sets password by email. */
  async createManual(payload: {
    name: string;
    owner_email: string;
    owner_name?: string;
    cnpj?: string;
    uf?: string;
  }): Promise<{ workspace_id: number; owner_email_masked: string; set_password_url: string }> {
    return unwrap(api.post("/api/v1/admin/workspaces", payload));
  },

  async setAddress(id: number, payload: WorkspaceAddress): Promise<WorkspaceAddress> {
    return unwrap(api.put(`${BASE}/${id}/address`, payload));
  },

  async update(id: number, payload: WorkspaceUpdatePayload): Promise<Workspace> {
    return unwrap(api.patch(`${BASE}/${id}`, payload));
  },

  async remove(id: number): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  },

  async getStats(id: number): Promise<WorkspaceStats> {
    return unwrap(api.get(`${BASE}/${id}/stats`));
  },

  async getSettings(id: number): Promise<WorkspaceSettings> {
    return unwrap(api.get(`${BASE}/${id}/settings`));
  },

  async updateSettings(
    id: number,
    payload: WorkspaceSettingsUpdatePayload,
  ): Promise<WorkspaceSettings> {
    return unwrap(api.patch(`${BASE}/${id}/settings`, payload));
  },
};

export const workspacePlanService = {
  async list(): Promise<WorkspacePlan[]> {
    return unwrap(api.get(`${BASE}/plans`));
  },

  async create(payload: WorkspacePlanCreatePayload): Promise<WorkspacePlan> {
    return unwrap(api.post(`${BASE}/plans`, payload));
  },

  async update(id: number, payload: WorkspacePlanUpdatePayload): Promise<WorkspacePlan> {
    return unwrap(api.patch(`${BASE}/plans/${id}`, payload));
  },
};
